/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import {CTCEntity, Entity, Link, Role} from '../types/entities';
import {contains} from '../utils/StringUtil';
import {defaultRuleValidator, LabelRulesValidator} from './LabelRulesValidator';

export type EntityInstance = CTCEntity | Link;

/**
 * EntityLabel API is used to return an entity label based on a user's role.
 * The EntityLabel checks the user's role at initialization of the app.
 *
 * ```typescript
 * import LabelRulesValidator from '@ion/core/src/entities/EntityLabel';
 *
 * <GlobalGroupChooser = ({...props, selectedGroup}) => {
 * ...
 *  const accountName = EntityLabel.get(selectedGroup)
 *  return...
 * }
 * ```
 */
class EntityLabel {
  /**
   * Defines the reference to the authenticated user's role which will be
   * used for determining the rules by which labels are to be returned.
   */
  private _role: Role = null

  /**
   * Defines the reference to the validator against labels will be returned.
   */
  private _validator: LabelRulesValidator = defaultRuleValidator

  /**
   * getRole returns the role defined at initialization.
   * @returns Role
   */
  getRole(): Role {
    return this._role;
  }

  /**
   * getValidator returns the rules validator method defined at initialization.
   * @returns LabelRulesValidator
   */
  getValidator() {
    return this._validator;
  }

  /**
   * The initialize method sets the role and optional validator to be used by the EntityLabel API
   *
   * ```typescript
   * const initializeApp = () => {
   *   const role = resolveRole(user, roles)
   * ...
   *   EntityLabel.initialize(role)
   * ...
   * }
   * ```
   * @param role user's role
   * @param validator optional validator method to define distinct label rules
   */
  initialize = (role: Role, validator?: LabelRulesValidator) => {
    this._role = role;
    this._validator = validator || defaultRuleValidator;
  }

  /**
   * get returns a label string based on the entity and the established validator rules.
   * ```typescript
   * import EntityLabel from '@ion/core/src/entities/EntityLabel';
   *
   * <GlobalGroupChooser = ({...props, selectedGroup}) => {
   * ...
   *  const accountName = EntityLabel.get(selectedGroup)
   *
   * ```
   *
   * @param entity entity data to use when creating a label.
   * @param validator optional method to define distinct label rules.
   * @returns string
   */
  get = (entity: CTCEntity | Link, validator?: LabelRulesValidator) => {
    if (entity) {
      const type     = EntityLabel.getEntityType(entity);
      const label    = EntityLabel.getEntityLabel(entity);
      const verifier = this._validator || validator;
      return verifier({
        entity,
        type,
        label,
        role : this._role
      });
    }
    return '';
  }

  /**
   * getLabels retrieves the labels for a given array of entities.
   * 
   * This function checks if the entity array is undefined or empty.
   * If not, it iterates over the array and looks for each entity's `href`.
   * For each entity that has an `href`, it retrieves the corresponding label
   * using the `get` method and accumulates the labels into a comma-separated string.
   * 
   * @param {CTCEntity[]} entity - An array of entities for which to retrieve labels.
   */
  getLabels = (entity: CTCEntity[]) => {
    if (isUndefined(entity) || !entity.length) return '';
  
    return entity.reduce((acc: string[], item: CTCEntity) => {
      if (item?.href) {
        const label = this.get(item);
        label && acc.push(label);
      }
      return acc;
    }, []).join(', ');
  };

  /**
   * getEntityType sets the entity type based on entity.href or
   * entity._self.href in order to structure the returned label.
   *
   * After splitting href, we check the href elements against the
   * Entity Enum types; then we convert that plural href element (i.e. 'accounts')
   * in its singular version ('account') which we use to resolve the entity label
   * returned from EntityLabel.get().
   */
  private static getEntityType(entity: EntityInstance): Entity | undefined {
    const result = entity?.href || entity?._self.href;
    if (result) {
      const types = Object.values(Entity);
      const parts = result.split('/');
      const type  = parts.find((part: string) => {
        return types.find((type: Entity) => contains(part, type));
      });
      return type?.slice(0, -1);
    }
  }

  /**
   * getEntityLabel gets the entity tite or name; this label is returned
   * from EntityLabel.get() when passing in an unrestricted role (default behavior).
   */
  private static getEntityLabel(entity: CTCEntity): Entity {
    return entity?.title || entity?.name;
  }
}

export default new EntityLabel();
